// src/utils/checkPubSupport.ts

import { 
    getPubByGooglePlaceId, 
    checkSolarCoverage, 
    createPub 
} from '../API';
import { GooglePubResult } from '../components/PubSearchGoogle';
import extractCity from './cityExtractor';

/**
 * Defines the structure of a pub object ready for navigation.
 * This should match the object you pass in `navigate('/pub/...', { state: { pubToView: ... } })`
 */
export interface PubForNavigation {
    google_place_id: string;
    name: string;
    address: string;
    latitude: number;
    longitude: number;
    garden_drawn: boolean;
    garden_moderated: boolean;
    has_no_garden: boolean;
    city: string | null;
    distance: number;
    is_2d_supported: boolean | null;
    _2d_support_level: string | null;
}

const UNSUPPORTED_2D_MESSAGE = "We're sorry, this pub isn't currently supported. Please try another pub.";
const UNSUPPORTED_LOCATION_MESSAGE = "We're sorry, this location isn't currently supported. Please try another pub.";
const INVALID_COORDS_MESSAGE = "Invalid location coordinates. Please try another pub.";
const GENERIC_ERROR_MESSAGE = "An unexpected error occurred. Please try again.";

/**
 * Processes a pub from a Google search, checking for its existence and support levels (2D/3D).
 * It creates or updates the pub in the database as needed.
 * 
 * @param googlePub The pub result from the Google Places search.
 * @param onProgress An optional callback to send loading/progress messages to the UI.
 * @returns A promise that resolves with a fully populated `PubForNavigation` object.
 * @throws An error with a user-friendly message if the pub is unsupported or an issue occurs.
 */
export const checkPubSupport = async (
    googlePub: GooglePubResult,
    onProgress?: (message: string) => void
): Promise<PubForNavigation> => {
    // 1. Initial Validation
    if (!googlePub.latitude || !googlePub.longitude || isNaN(googlePub.latitude) || isNaN(googlePub.longitude)) {
        throw new Error(INVALID_COORDS_MESSAGE);
    }

    try {
        const existingPubResponse = await getPubByGooglePlaceId(googlePub.google_place_id);

        // --- BRANCH 1: PUB ALREADY EXISTS IN OUR DATABASE ---
        if (existingPubResponse.data && existingPubResponse.data.exists) {
            let pub = existingPubResponse.data.pub;
            let needsDbUpdate = false;

            // Priority 1: Check 2D Support
            if (pub.is_2d_supported === false) {
                throw new Error(UNSUPPORTED_2D_MESSAGE);
            }
            if (pub.is_2d_supported === null || pub.is_2d_supported === undefined) {
                onProgress?.("Checking 2D coverage...");
                const solarCoverage = await checkSolarCoverage(googlePub.latitude, googlePub.longitude);
                if (!solarCoverage.has_coverage) {
                    // Update DB to mark as unsupported so we don't check again
                    await createPub({ pub: { ...pub, is_2d_supported: false } });
                    throw new Error(UNSUPPORTED_2D_MESSAGE);
                }
                pub.is_2d_supported = true;
                pub._2d_support_level = solarCoverage.quality || 'NONE';
                needsDbUpdate = true;
            }

            // If we added new support info, save it
            if (needsDbUpdate) {
                onProgress?.("Updating pub information...");
                await createPub({ pub });
            }
            
            // Construct and return the final object
            return {
                google_place_id: pub.google_place_id,
                name: googlePub.name,
                address: googlePub.formatted_address || googlePub.address || '',
                latitude: googlePub.latitude,
                longitude: googlePub.longitude,
                garden_drawn: pub.garden_drawn || false,
                garden_moderated: pub.garden_moderated || false,
                has_no_garden: pub.has_no_garden || false,
                city: pub.city || extractCity(googlePub),
                distance: 0, // Distance is calculated on list pages, not here
                is_2d_supported: pub.is_2d_supported,
                _2d_support_level: pub._2d_support_level
            };
        }

        // --- BRANCH 2: NEW PUB ---
        else {
            onProgress?.("New venue found. Checking support...");
            
            // Critical Step: Check 2D support first.
            const solarCoverage = await checkSolarCoverage(googlePub.latitude, googlePub.longitude);
            if (!solarCoverage.has_coverage) {
                throw new Error(UNSUPPORTED_LOCATION_MESSAGE);
            }
            
            // Create new pub in the DB
            onProgress?.("Adding pub to our database...");
            const city = extractCity(googlePub);
            const createPubPayload = {
                pub: {
                    google_place_id: googlePub.google_place_id,
                    city: city,
                    is_2d_supported: solarCoverage.has_coverage,
                    _2d_support_level: solarCoverage.quality || 'NONE',
                    garden_drawn: false,
                    garden_moderated: false,
                    has_no_garden: false,
                }
            };
            const createResponse = await createPub(createPubPayload);
            
            if (createResponse.status !== 201) {
                throw new Error("Failed to add the pub to our database.");
            }

            // Construct and return the final object
            return {
                google_place_id: googlePub.google_place_id,
                name: googlePub.name,
                address: googlePub.formatted_address || googlePub.address || '',
                latitude: googlePub.latitude,
                longitude: googlePub.longitude,
                garden_drawn: false,
                garden_moderated: false,
                has_no_garden: false,
                city: city,
                distance: 0,
                is_2d_supported: solarCoverage.has_coverage,
                _2d_support_level: solarCoverage.quality || 'NONE',
            };
        }
    } catch (error: any) {
        // Log the technical error, but re-throw a user-friendly one.
        console.error('Error in checkPubSupport:', error);
        // If the error already has a user-friendly message, use it. Otherwise, use a generic one.
        if (error.message && [UNSUPPORTED_2D_MESSAGE, UNSUPPORTED_LOCATION_MESSAGE, INVALID_COORDS_MESSAGE].includes(error.message)) {
            throw error;
        }
        throw new Error(GENERIC_ERROR_MESSAGE);
    }
};